// src/components/SokuhouUriagePanel.tsx — 速報売上(会計別)(ベータ・2026-07-13)
//   会計RP請求金額付与PJ「明細再現チェーン」の速報出力を可視化。3ビュー:
//     パルス   … 日次vstack(手技/薬剤/材料/DPC包括[n系90])。補正なし生値・直近2日は淡色。
//     KCODE    … 束カード(PCI/ABL等)+KCODE別ランキング(件数/出来高/請求/倍率寄与・前月比)。
//                集計キー=医事コード→倍率(深夜1.8等)でも同一KCODEに合算。
//     RPドリル … KCODEクリックで会計(RP)単位まで(出来高/請求/3分解/倍率寄与)。
//   確度の前提(実測): D+1入力済=入院74%/外来81-87%・「入っている値」の訂正は-0.04%。
//   出来高=包括前満額(明細再現の金額合計)・請求=行為別集計GUI(包括吸収は0)。DPC包括=n系90(日次バッチ)。
//   ★パルスの手技/薬剤/材料=請求側(包括吸収0)+DPC別レイヤ=二重計上なし。KCODE表は出来高と請求の両方。
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { SokuhouDailyRow, SokuhouKcodeRow, SokuhouRpRow } from "@shared/api";
import { useSort, SortTh } from "../lib/useSort";

const oku = (v: number | null | undefined) =>
  v == null ? "" : (v / 1e8).toFixed(2) + "億円";
const man = (v: number | null | undefined) =>
  v == null ? "" : Math.round(v / 1e4).toLocaleString("ja-JP") + "万円";
const yenf = (v: number | null | undefined) =>
  v == null || v === 0 ? "—" : Math.abs(v) >= 1e8 ? oku(v) : man(v);

// KCODE束プリセット(「PCI何件何円」用。先頭一致で束ねる)
const BUNDLES: { name: string; prefixes: string[] }[] = [
  { name: "PCI", prefixes: ["K546", "K547", "K548", "K549"] },
  { name: "アブレーション", prefixes: ["K595"] },
  { name: "ペースメーカ", prefixes: ["K597", "K598", "K599"] },
  { name: "TAVI/弁", prefixes: ["K555", "K554", "K5551"] },
  { name: "脳血管内", prefixes: ["K178"] },
  { name: "内視鏡的切除", prefixes: ["K653", "K721"] },
];

type View = "パルス" | "KCODE" | "RPドリル";

export function SokuhouUriagePanel() {
  const [view, setView] = useState<View>("パルス");
  const [drillKcode, setDrillKcode] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["sokuhou_uriage"], queryFn: api.sokuhouUriage });

  const yms = useMemo(() => {
    const s = new Set((q.data?.daily ?? []).map((r) => r.ym));
    return [...s].sort();
  }, [q.data]);
  const curYm = yms[yms.length - 1] ?? "";
  const prevYm = yms.length > 1 ? yms[yms.length - 2] : "";

  if (q.isLoading) return <div className="p-8 text-slate-500">読込中…</div>;
  if (q.error || !q.data) return <div className="p-8 text-red-600">読込エラー: {String(q.error)}</div>;
  if (!q.data.daily.length)
    return <div className="p-8 text-slate-500">速報売上データ未投入(build_sokuhou_uriage.R を実行してください)</div>;

  const meta = q.data.meta.find((m) => m.ym === curYm);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-bold text-slate-800">
          速報売上(会計別) <span className="text-sm font-normal text-slate-500">{curYm.slice(2, 4)}年{curYm.slice(4)}月</span>
        </h2>
        <div className="flex rounded-lg border border-slate-300 overflow-hidden text-sm">
          {(["パルス", "KCODE", "RPドリル"] as View[]).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 py-1.5 ${view === v ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {v}
            </button>
          ))}
        </div>
        {meta && (
          <span className="text-xs text-slate-400">
            生成 {meta.generated_at}({meta.basis})・補正なし生値・D+1入力済=入院74%/外来81-87%
          </span>
        )}
      </div>
      {view === "パルス" && <PulseView daily={q.data.daily} curYm={curYm} />}
      {view === "KCODE" && (
        <KcodeView kcode={q.data.kcode} curYm={curYm} prevYm={prevYm}
          onDrill={(k) => { setDrillKcode(k); setView("RPドリル"); }} />
      )}
      {view === "RPドリル" && <RpDrillView ym={curYm} kcode={drillKcode} onClear={() => setDrillKcode(null)} />}
    </div>
  );
}

// ── パルス: 日次vstack(手技/薬剤/材料/DPC包括) ────────────────────────────
function PulseView({ daily, curYm }: { daily: SokuhouDailyRow[]; curYm: string }) {
  const days = useMemo(() => {
    const m = new Map<string, { 手技: number; 薬剤: number; 材料: number; DPC: number }>();
    for (const r of daily) {
      if (r.ym !== curYm) continue;
      const d = m.get(r.診療日) ?? { 手技: 0, 薬剤: 0, 材料: 0, DPC: 0 };
      d.手技 += r.手技円; d.薬剤 += r.薬剤円; d.材料 += r.材料円; d.DPC += r.DPC包括円;
      m.set(r.診療日, d);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
      .map(([日, v]) => ({ 日, ...v, 計: v.手技 + v.薬剤 + v.材料 + v.DPC }));
  }, [daily, curYm]);

  const tot = days.reduce((s, d) => s + d.計, 0);
  const totDpc = days.reduce((s, d) => s + d.DPC, 0);
  const max = Math.max(...days.map((d) => d.計), 1);
  const lastIdx = days.length - 1;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={`売上(請求)累計(${days.length}日)`} value={oku(tot)} sub="手技薬剤材料(包括吸収0)+DPC包括" />
        <Kpi label="日平均" value={man(tot / Math.max(days.length, 1))} />
        <Kpi label="うちDPC包括(n系90・日次計上)" value={oku(totDpc)} sub="出来高でなく請求(実売上)ベース" />
        <Kpi label="確度メモ" value="直近2日=入力途上" sub="淡色表示。D+3で85-92%・締めで100%" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-end gap-1" style={{ height: 240 }}>
          {days.map((d, i) => (
            <div key={d.日} className={`flex-1 flex flex-col-reverse relative ${i >= lastIdx - 1 ? "opacity-40" : ""}`}
              title={`${d.日}: 計${man(d.計)}(手技${man(d.手技)}/薬剤${man(d.薬剤)}/材料${man(d.材料)}/DPC${man(d.DPC)})`}>
              <div className="bg-blue-900" style={{ height: (d.手技 / max) * 200 }} />
              <div className="bg-blue-600" style={{ height: (d.薬剤 / max) * 200 }} />
              <div className="bg-blue-300" style={{ height: (d.材料 / max) * 200 }} />
              <div className="bg-purple-300" style={{ height: (d.DPC / max) * 200 }} />
              <div className="absolute -bottom-5 w-full text-center text-[10px] text-slate-500">{+d.日.slice(6)}</div>
            </div>
          ))}
        </div>
        <div className="mt-7 text-xs text-slate-500">
          <Sw c="bg-blue-900" t="手技ほか(手技/入院料/自費/食事等)" /> <Sw c="bg-blue-600" t="薬剤" /> <Sw c="bg-blue-300" t="材料" />
          <Sw c="bg-purple-300" t="DPC包括(n系90)" />
          <span className="ml-3">請求(実売上・包括吸収0)+DPC包括・入外計・淡色=入力途上(D+1実測: 入院74%/外来81-87%)</span>
        </div>
      </div>
    </div>
  );
}

// ── KCODE: 束カード + ランキング(前月比) ─────────────────────────────────
function KcodeView({ kcode, curYm, prevYm, onDrill }:
  { kcode: SokuhouKcodeRow[]; curYm: string; prevYm: string; onDrill: (k: string) => void }) {
  const agg = useMemo(() => {
    const by = (ym: string) => {
      const m = new Map<string, { 件数: number; 出来高円: number; 請求円: number; 倍率寄与円: number; 名称: string }>();
      for (const r of kcode) {
        if (r.ym !== ym) continue;
        const d = m.get(r.KCODE) ?? { 件数: 0, 出来高円: 0, 請求円: 0, 倍率寄与円: 0, 名称: r.名称 };
        d.件数 += r.件数; d.出来高円 += r.出来高円; d.請求円 += r.請求円; d.倍率寄与円 += r.倍率寄与円;
        m.set(r.KCODE, d);
      }
      return m;
    };
    return { cur: by(curYm), prev: by(prevYm) };
  }, [kcode, curYm, prevYm]);

  const bundles = BUNDLES.map((b) => {
    let 件数 = 0, 円 = 0;
    for (const [k, v] of agg.cur) if (b.prefixes.some((p) => k.startsWith(p))) { 件数 += v.件数; 円 += v.出来高円; }
    return { ...b, 件数, 円 };
  }).filter((b) => b.件数 > 0);

  const rows = useMemo(() => {
    return [...agg.cur.entries()]
      .filter(([k]) => k.startsWith("K"))
      .map(([k, v]) => ({ KCODE: k, ...v, 前月円: agg.prev.get(k)?.出来高円 ?? 0 }))
      .sort((a, b) => b.出来高円 - a.出来高円).slice(0, 40);
  }, [agg]);
  const s = useSort(rows);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {bundles.map((b) => (
          <div key={b.name} className="bg-blue-900 text-white rounded-lg px-4 py-3">
            <div className="text-xs opacity-80">{b.name}</div>
            <div className="text-2xl font-bold">{b.件数}<span className="text-sm font-normal"> 件</span></div>
            <div className="text-sm opacity-90">{yenf(b.円)}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
        <div className="text-sm font-semibold text-slate-700 mb-2">
          手術KCODE別売上(上位40・当月出来高順=包括前満額) <span className="font-normal text-xs text-slate-400">クリックでRPドリル。出来高=包括前満額/請求=DPC吸収後(差=DPC包括分)。倍率寄与=深夜等加算(減算は負)。キー=医事コード</span>
        </div>
        <table className="min-w-full text-sm">
          <thead><tr className="border-b-2 border-blue-900 text-xs text-slate-600">
            <SortTh s={s} sortKey="KCODE" get={(r) => (r as (typeof rows)[number]).KCODE} className="text-left px-2 py-1">KCODE</SortTh>
            <th className="text-left px-2 py-1">名称</th>
            <SortTh s={s} sortKey="件数" get={(r) => (r as (typeof rows)[number]).件数} className="text-right px-2 py-1">件数</SortTh>
            <SortTh s={s} sortKey="出来高円" get={(r) => (r as (typeof rows)[number]).出来高円} className="text-right px-2 py-1">出来高</SortTh>
            <SortTh s={s} sortKey="請求円" get={(r) => (r as (typeof rows)[number]).請求円} className="text-right px-2 py-1">請求(包括/院外0)</SortTh>
            <SortTh s={s} sortKey="倍率寄与円" get={(r) => (r as (typeof rows)[number]).倍率寄与円} className="text-right px-2 py-1">倍率寄与</SortTh>
            <SortTh s={s} sortKey="前月円" get={(r) => (r as (typeof rows)[number]).前月円} className="text-right px-2 py-1">前月出来高</SortTh>
          </tr></thead>
          <tbody>
            {s.rows.map((r) => (
              <tr key={r.KCODE} onClick={() => onDrill(r.KCODE)}
                className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer">
                <td className="px-2 py-1 font-mono text-xs">{r.KCODE}</td>
                <td className="px-2 py-1">{r.名称}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.件数}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.出来高円)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.請求円)}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${r.倍率寄与円 < 0 ? "text-orange-600" : ""}`}>{yenf(r.倍率寄与円)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-slate-400">{yenf(r.前月円)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── RPドリル: 会計(RP)単位 ────────────────────────────────────────────────
function RpDrillView({ ym, kcode, onClear }: { ym: string; kcode: string | null; onClear: () => void }) {
  const q = useQuery({
    queryKey: ["sokuhou_uriage_rp", ym, kcode],
    queryFn: () => api.sokuhouUriageRp({ ym, kcode: kcode ?? undefined }),
  });
  const rows = q.data?.rows ?? [];
  const s = useSort(rows);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="text-sm font-semibold text-slate-700">
          会計(RP)ドリル {kcode ? <span className="font-mono">{kcode}</span> : "(全体・出来高上位5000)"}
        </div>
        {kcode && <button onClick={onClear} className="text-xs text-blue-700 underline">絞り込み解除</button>}
      </div>
      {q.isLoading ? <div className="text-slate-500 p-4">読込中…</div> : (
        <table className="min-w-full text-sm">
          <thead><tr className="border-b-2 border-blue-900 text-xs text-slate-600">
            <SortTh s={s} sortKey="診療日" get={(r) => (r as SokuhouRpRow).診療日} className="text-left px-2 py-1">診療日</SortTh>
            <th className="text-left px-2 py-1">KCODE</th>
            <th className="text-left px-2 py-1">名称(主たる手技)</th>
            <SortTh s={s} sortKey="診療科" get={(r) => (r as SokuhouRpRow).診療科} className="text-left px-2 py-1">科</SortTh>
            <SortTh s={s} sortKey="出来高円" get={(r) => (r as SokuhouRpRow).出来高円} className="text-right px-2 py-1">出来高</SortTh>
            <SortTh s={s} sortKey="請求円" get={(r) => (r as SokuhouRpRow).請求円} className="text-right px-2 py-1">請求</SortTh>
            <SortTh s={s} sortKey="手技円" get={(r) => (r as SokuhouRpRow).手技円} className="text-right px-2 py-1">手技</SortTh>
            <SortTh s={s} sortKey="薬剤円" get={(r) => (r as SokuhouRpRow).薬剤円} className="text-right px-2 py-1">薬剤</SortTh>
            <SortTh s={s} sortKey="材料円" get={(r) => (r as SokuhouRpRow).材料円} className="text-right px-2 py-1">材料</SortTh>
            <SortTh s={s} sortKey="倍率寄与円" get={(r) => (r as SokuhouRpRow).倍率寄与円} className="text-right px-2 py-1">倍率寄与</SortTh>
          </tr></thead>
          <tbody>
            {s.rows.slice(0, 300).map((r) => (
              <tr key={r.Rpid} className="border-b border-slate-100">
                <td className="px-2 py-1 tabular-nums">{r.診療日.slice(4, 6)}/{r.診療日.slice(6)}</td>
                <td className="px-2 py-1 font-mono text-xs">{r.KCODE}</td>
                <td className="px-2 py-1">{r.名称}</td>
                <td className="px-2 py-1">{r.診療科}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.出来高円)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.請求円)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.手技円)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.薬剤円)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{yenf(r.材料円)}</td>
                <td className={`px-2 py-1 text-right tabular-nums ${r.倍率寄与円 < 0 ? "text-orange-600" : ""}`}>{yenf(r.倍率寄与円)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="mt-2 text-xs text-slate-400">表示は上位300行(データは5000行まで取得)。患者情報は含まない(RP粒度)。</div>
    </div>
  );
}

const Kpi = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="bg-white border border-slate-200 rounded-lg px-4 py-3">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-xl font-bold text-blue-900 mt-0.5">{value}</div>
    {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);
const Sw = ({ c, t }: { c: string; t: string }) => (
  <span className="mr-1"><span className={`inline-block w-3 h-3 ${c} align-[-1px] mr-1`} />{t}</span>
);
